import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { RappiderButtonModule } from '@rappider/rappider-components';
import { RappiderListGridModule } from '@rappider/rappider-components';
import { RappiderAlertModule } from '@rappider/rappider-components';
import { ButtonType, ButtonSize } from '@rappider/rappider-components/utils';
import { AIResponseStructure } from '../../../utils/ai-response-types.interface';
import { dataModelListGridConfig, currentModelListGridConfig } from './utils/data-model-list-grid.config';
import { relationsListGridConfig } from './utils/relations-list-grid.config';
import { AIChatGeneratorService } from '../../../services/ai-generator.service';
import { DataModelService } from '../../../services/data-model.service';
import { Store } from '@ngrx/store';
import { selectActiveProjectId } from 'libs/project/src/lib/states/active-project-state/active-project.selectors';
import { ProjectModelWithRelations } from '@rappider/rappider-sdk';
import { getProjectModelsWithFields } from '@rappider/shared';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AlertTypes } from '@rappider/rappider-components/utils';

/**
 * AI Data Model Response Renderer Component
 *
 * Renders AI-generated data models and their relations in a structured format.
 * Displays model fields and relations in separate list-grid tables.
 */
@Component({
  selector: 'rappider-ai-data-model-response-renderer',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzButtonModule,
    NzSpinModule,
    NzToolTipModule,
    RappiderButtonModule,
    RappiderListGridModule,
    RappiderAlertModule
  ],
  templateUrl: './ai-data-model-response-renderer.component.html',
  styleUrls: ['./ai-data-model-response-renderer.component.scss']
})
export class AiDataModelResponseRendererComponent implements OnInit, OnDestroy, OnChanges {

  @Input() responseData: AIResponseStructure | AIResponseStructure[] | null = null;
  @Input() message: unknown = null;
  @Input() showActions = true;

  @Output() save = new EventEmitter<AIResponseStructure | AIResponseStructure[]>();
  @Output() splitterToggleRequested = new EventEmitter<boolean>();

  readonly ButtonType = ButtonType;
  readonly ButtonSize = ButtonSize;
  readonly Array = Array;
  readonly AlertTypes = AlertTypes;

  listGridConfig = dataModelListGridConfig;
  currentModelListGridConfig = currentModelListGridConfig;
  relationsListGridConfig = relationsListGridConfig;
  activeProjectId?: string;
  private destroy$ = new Subject<void>();
  isSaving = false;
  isUpdatingModel = false;
  currentProjectModel?: ProjectModelWithRelations;
  actionResponseData?: Record<string, unknown> | unknown[];
  hasAttemptedSave = false;
  existingProjectModels: ProjectModelWithRelations[] = [];
  createdModelNames = new Set<string>();
  currentSavingModelName?: string;
  isSavingRelationsOnly = false;
  areRelationsCreated = false;
  isModelUpdatedSuccessfully = false;

  constructor(
    private aiChatGeneratorService: AIChatGeneratorService,
    private dataModelService: DataModelService,
    private store: Store
  ) {
    // Constructor intentionally left empty
  }

  ngOnInit(): void {
    this.subscribeToActiveProject();
    this.subscribeToSaveActions();
    this.subscribeToProjectModels();

    if (this.hasActions()) {
      this.updateProcessDataModel();
    }
  }

  ngOnChanges(): void {
    this.createdModelNames.clear();
    this.areRelationsCreated = false;
    this.isModelUpdatedSuccessfully = false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Retrieves the list of data models from the AI response
   * @returns Array of data model objects from the generated response
   */
  getModels(): unknown[] {
    if (!this.responseData) {
      return [];
    }

      const dataObj = this.responseData as Record<string, unknown>;
    const generated = dataObj.generated as Record<string, unknown>;
    const models = generated?.models && Array.isArray(generated.models) ? generated.models : [];
    return models;
  }

  /**
   * Extracts the display name from a data model object
   * @param data - The data model object
   * @returns The model name or default fallback text
   */
  getDisplayName(data: unknown): string {
    const dataObj = data as Record<string, unknown>;
    return (dataObj.modelName as string) || 'Generated Content';
  }

  /**
   * Extracts the description from a data model object
   * @param data - The data model object
   * @returns The model description or default fallback text
   */
  getDescription(data: unknown): string {
    const dataObj = data as Record<string, unknown>;
    return (dataObj.description as string) || 'AI Generated Content';
  }

  /**
   * Generates a header title from the list of data models
   * @returns Comma-separated list of model names or empty string for single/no models
   */
  getHeaderTitle(): string {
    const models = this.getModels();
    if (models.length <= 1 || !models) {
      return '';
    }

    const itemNames = models
      .map(item => this.getDisplayName(item))
      .filter(name => name !== 'N/A' && name !== 'Generated Content');

    if (itemNames.length > 0) {
      return itemNames.join(', ');
    }

    return '';
  }

  /**
   * Checks if the response data is valid and contains models or relations
   * @returns True if response is valid and has models or relations
   */
  isValidResponse(): boolean {
    if (!this.responseData) {
      return false;
    }

    const models = this.getModels();
    const hasModels = models.length > 0;
    const hasRelations = this.hasRelations();
    const hasActions = this.hasActions();
    const hasCurrentModel = !!this.currentProjectModel;
    const hasNoChanges = this.hasNoChanges();
    return hasModels || hasRelations || hasActions || hasCurrentModel || hasNoChanges;
  }

  /**
   * Checks if there are any models to display
   * @returns True if there are models
   */
  hasModels(): boolean {
    const models = this.getModels();
    return models.length > 0;
  }

  /**
   * Gets the appropriate button text based on content type
   * @param content - The content object (can be null for relations-only case)
   * @returns Button text string
   */
  getButtonText(content: unknown): string {
    const hasModels = this.hasModels();
    const hasRelations = this.hasRelations();

    if (hasModels && hasRelations) {
      // Both models and relations exist
      if (content) {
        const modelName = this.getDisplayName(content);
        const hasModelRelations = this.getRelationsForModel(modelName).length > 0;
        if (hasModelRelations) {
          return `Create ${modelName} Data Model & Relations`;
        } else {
          return `Create ${modelName} Data Model`;
        }
      } else {
        return 'Create Data Model & Relations';
      }
    } else if (hasModels && !hasRelations) {
      // Only models exist
      if (content) {
        const modelName = this.getDisplayName(content);
        return `Create ${modelName} Data Model`;
      } else {
        return 'Create Data Model';
      }
    } else if (!hasModels && hasRelations) {
      // Only relations exist
      return 'Create Relations';
    } else {
      return 'Create Content';
    }
  }

  /**
   * Checks if the AI response contains relations data
   * @returns True if relations exist and are not empty
   */
  hasRelations(): boolean {
    const dataObj = this.responseData as Record<string, unknown>;
    const generated = dataObj.generated as Record<string, unknown>;
    const hasRels = !!(generated?.relations && Array.isArray(generated.relations) && generated.relations.length > 0);
    return hasRels;
  }

  /**
   * Checks if the AI response contains actions data
   * @returns True if actions exist and are not empty
   */
  hasActions(): boolean {
    if (!this.responseData || typeof this.responseData !== 'object') {
      return false;
    }

    const dataObj = this.responseData as Record<string, unknown>;
    const actions = dataObj.actions || (dataObj.generated as Record<string, unknown>)?.actions;
    return !!(actions && Array.isArray(actions) && actions.length > 0);
  }

  /**
   * Gets the actions array from the response data
   * @returns Array of actions or empty array
   */
  getActions(): unknown[] {
    if (!this.responseData || typeof this.responseData !== 'object') {
      return [];
    }

    const dataObj = this.responseData as Record<string, unknown>;
    const actions = dataObj.actions || (dataObj.generated as Record<string, unknown>)?.actions;
    return Array.isArray(actions) ? actions : [];
  }

  /**
   * Retrieves relations for a specific data model
   * @param modelName - The name of the model to get relations for
   * @returns Array of relations where the model is either source or target
   */
  getRelationsForModel(modelName: string): unknown[] {
    const dataObj = this.responseData as Record<string, unknown>;
    const generated = dataObj.generated as Record<string, unknown>;
    const relations = generated?.relations as unknown[] || [];

    const filteredRelations = relations.filter((relation: unknown) => {
      const rel = relation as Record<string, unknown>;
      const isMatch = rel.sourceModel === modelName || rel.targetModel === modelName;
      return isMatch;
    });

    return filteredRelations;
  }

  /**
   * Formats a relation object for display in the relations table
   * @param relation - The relation object to format
   * @returns Formatted relation object with display-friendly values
   */
  formatRelationForDisplay(relation: unknown): Record<string, unknown> {
    const rel = relation as Record<string, unknown>;
    return {
      name: rel.name || '',
      sourceModel: rel.sourceModel || '',
      type: rel.type || '',
      targetModel: rel.targetModel || '',
      throughModel: rel.throughModel || '',
      description: rel.description || ''
    };
  }

  /**
   * Gets formatted relations for a specific model ready for display
   * @param modelName - The name of the model to get formatted relations for
   * @returns Array of formatted relation objects for display
   */
  getFormattedRelationsForModel(modelName: string): Record<string, unknown>[] {
    const relations = this.getRelationsForModel(modelName);
    const formatted = relations.map(relation => this.formatRelationForDisplay(relation));
    return formatted;
  }

  /**
   * Gets all formatted relations for display when only relations exist
   * @returns Array of all formatted relation objects for display
   */
  getAllFormattedRelations(): Record<string, unknown>[] {
    const dataObj = this.responseData as Record<string, unknown>;
    const generated = dataObj.generated as Record<string, unknown>;
    const relations = generated?.relations as unknown[] || [];

    const formatted = relations.map(relation => this.formatRelationForDisplay(relation));
    return formatted;
  }


  /**
   * Handles the save button click event for creating data models
   * @param responseItem - The AI response structure to save (can be null for relations-only case)
   */
  onSaveClick(responseItem: AIResponseStructure | null | unknown): void {
    if (!this.activeProjectId || this.isSaving || !this.responseData || typeof this.responseData !== 'object') {
      return;
    }

    this.hasAttemptedSave = true;
    if (this.hasRelations() && this.hasMissingModelsForRelations()) {
      return;
    }

    const isRelationsOnly = !responseItem || typeof responseItem !== 'object';
    const modelName = isRelationsOnly ? null : this.extractModelName(responseItem);

    this.isSaving = true;
    this.splitterToggleRequested.emit(true);

    const dataToSave = this.filterResponseDataForModel(responseItem);
    this.aiChatGeneratorService.saveGeneratedDataModelsWithRelations(dataToSave, this.activeProjectId);

    if (modelName) {
      this.currentSavingModelName = modelName;
    } else if (isRelationsOnly) {
      this.isSavingRelationsOnly = true;
    }

    if (!isRelationsOnly) {
      this.save.emit(responseItem as AIResponseStructure);
    }
  }

  /**
 * Extracts and formats field data from a data model for display
 * @param model - The data model object containing exampleData
 * @returns Array of formatted field objects for the data model table
 */
  private updateProcessDataModel(): void {
    if (!this.responseData) {
      return;
    }

    this.isUpdatingModel = true;

    this.dataModelService.updateProcessDataModelFromResponse(this.responseData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.isUpdatingModel = false;
          if (result && typeof result === 'object') {
            const resultObj = result as Record<string, unknown>;
            this.actionResponseData = resultObj.dataModelSampleJSON as Record<string, unknown> | unknown[];
            const projectModelId = resultObj.projectModelId as string;
            if (projectModelId) {
              this.loadCurrentModelFromStore(projectModelId);
            }
          }
        },
        error: () => this.isUpdatingModel = false
      });
  }


  /**
   * Loads the current model from the store by projectModelId
   * @param projectModelId - The ID of the current model
   * @private
   */
  private loadCurrentModelFromStore(projectModelId: string): void {
    this.dataModelService.getCurrentModel(projectModelId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(currentModel => {
        if (currentModel) {
          this.currentProjectModel = currentModel;
        }
      });
  }

  /**
   * Gets the fields for a data model
   * @param model - The data model object containing exampleData
   * @returns Array of formatted field objects for the data model table
   */
  getDataModelFields(model: unknown): unknown[] {
    const modelObj = model as Record<string, unknown>;
    const exampleData = modelObj.exampleData as unknown[] || [];

    return exampleData.map(item => {
      const itemObj = item as Record<string, unknown>;
      const fieldName = Object.keys(itemObj).find(key => key !== '_description');
      const fieldValue = fieldName ? itemObj[fieldName] : null;

      return {
        fieldName: fieldName || 'N/A',
        sampleData: fieldValue,
        description: itemObj._description || 'N/A'
      };
    });
  }

  /**
   * Gets formatted fields combining current model and response data
   * @returns Array of formatted field objects with status indicators
   */
  getCurrentModelFields(): unknown[] {
    return this.dataModelService.getFormattedFields(
      this.currentProjectModel,
      this.actionResponseData
    );
  }

  /**
   * Handles update model button click
   */
  onUpdateModelClick(): void {
    if (!this.currentProjectModel || !this.actionResponseData || this.isSaving) {
      return;
    }

    this.isSaving = true;
    this.splitterToggleRequested.emit(true);
    const projectModelId = this.currentProjectModel.id;
    const dataModelSampleJSON = this.actionResponseData;

    this.dataModelService.updateDataModel(projectModelId, dataModelSampleJSON)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isSaving = false;
          this.isModelUpdatedSuccessfully = true;
          this.loadCurrentModelFromStore(projectModelId);
          this.clearActionResponseData();
          this.emitUpdateSuccess(projectModelId);
        },
        error: () => this.isSaving = false
      });
  }

  /**
   * Clears the action response data after successful update
   * @private
   */
  private clearActionResponseData(): void {
    this.actionResponseData = undefined;
  }

  /**
   * Emits update success event
   */
  private emitUpdateSuccess(projectModelId: string): void {
    const updateSuccessEvent = new CustomEvent('modelUpdateSuccess', {
      detail: { projectModelId: projectModelId }
    });
    window.dispatchEvent(updateSuccessEvent);
  }


  private subscribeToActiveProject(): void {
    this.store.select(selectActiveProjectId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(projectId => this.activeProjectId = projectId);
  }

  /**
   * Subscribes to project models from store for real-time updates
   * @private
   */
  private subscribeToProjectModels(): void {
    this.store.select(getProjectModelsWithFields)
      .pipe(takeUntil(this.destroy$))
      .subscribe(projectModels => {
        this.existingProjectModels = (projectModels as ProjectModelWithRelations[]) || [];
      });
  }


  /**
   * Subscribes to save action events (success/error) from the AI service
   * @private
   */
  private subscribeToSaveActions(): void {
    this.aiChatGeneratorService.subscribeToSaveSuccess()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.isSaving = false;
        if (this.currentSavingModelName) {
          this.createdModelNames.add(this.currentSavingModelName);
          this.currentSavingModelName = undefined;
        }
        if (this.isSavingRelationsOnly) {
          this.areRelationsCreated = true;
          this.isSavingRelationsOnly = false;
        }
      });

    this.aiChatGeneratorService.subscribeToSaveError()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.isSaving = false;
        this.currentSavingModelName = undefined;
        this.isSavingRelationsOnly = false;
      });
  }

  /**
   * Gets the action message from the response data
   * @returns The action message string
   */
  getActionMessage(): string {
    if (!this.hasActions()) {
      return 'Model will be updated';
    }

    const actions = this.getActions();
    if (actions.length === 0) {
      return 'Model will be updated';
    }

    const firstAction = actions[0] as Record<string, unknown>;
    const actionBody = firstAction.body as Record<string, unknown>;
    const message = actionBody?.message as string;

    return message || 'Model will be updated';
  }

  /**
   * Checks if there are missing models for relations
   * @returns True if any relation has missing source or target models
   */
  hasMissingModelsForRelations(): boolean {
    if (!this.hasRelations()) {
      return false;
    }

    const dataObj = this.responseData as Record<string, unknown>;
    const generated = dataObj.generated as Record<string, unknown>;
    const relations = generated?.relations as unknown[] || [];
    const models = this.getModels();

    if (!relations || relations.length === 0) {
      return false;
    }

    // Get model names from AI response
    const aiModelNames = models.map(model => this.getDisplayName(model));

    // Get model names from existing project models (real-time)
    const existingModelNames = this.existingProjectModels.map(model => model.name);

    // Combine both lists
    const allModelNames = [...aiModelNames, ...existingModelNames];

    return relations.some((relation: unknown) => {
      const rel = relation as Record<string, unknown>;
      const sourceModel = rel.sourceModel as string;
      const targetModel = rel.targetModel as string;

      return !allModelNames.includes(sourceModel) || !allModelNames.includes(targetModel);
    });
  }


  /**
   * Gets missing model names for relations
   * @returns Array of missing model names
   */
  getMissingModelNames(): string[] {
    if (!this.hasRelations()) {
      return [];
    }

    const dataObj = this.responseData as Record<string, unknown>;
    const generated = dataObj.generated as Record<string, unknown>;
    const relations = generated?.relations as unknown[] || [];
    const models = this.getModels();

    if (!relations || relations.length === 0) {
      return [];
    }

    // Get model names from AI response
    const aiModelNames = models.map(model => this.getDisplayName(model));

    const existingModelNames = this.existingProjectModels.map(model => model.name);

    const allModelNames = [...aiModelNames, ...existingModelNames];
    const missingModels = new Set<string>();

    relations.forEach((relation: unknown) => {
      const rel = relation as Record<string, unknown>;
      const sourceModel = rel.sourceModel as string;
      const targetModel = rel.targetModel as string;

      if (sourceModel && !allModelNames.includes(sourceModel)) {
        missingModels.add(sourceModel);
      }
      if (targetModel && !allModelNames.includes(targetModel)) {
        missingModels.add(targetModel);
      }
    });

    return Array.from(missingModels);
  }

  /**
   * Gets the missing models alert message with specific model names
   * @returns Alert message with missing model names
   */
  getMissingModelsMessage(): string {
    const missingModels = this.getMissingModelNames();

    if (missingModels.length === 0) {
      return '';
    }

    if (this.hasAttemptedSave) {
      // Danger message after button click
      if (missingModels.length === 1) {
        return `The relation cannot be created because the '${missingModels[0]}' data model is missing. ` +
               'Please create this data model first.';
      }
      const modelsList = missingModels.join(', ');
      return `Relations cannot be created because the following data models are missing: ${modelsList}. ` +
             'Please create these data models first.';
    } else {
      // Warning message before button click
      if (missingModels.length === 1) {
        return `The relation requires the '${missingModels[0]}' data model to be created first. ` +
               'Please create this data model before establishing relations.';
      }
      const modelsList = missingModels.join(', ');
      return `The relations require the following data models to be created first: ${modelsList}. ` +
             'Please create these data models before establishing relations.';
    }
  }

  /**
   * Checks if there are no changes detected in the data model comparison
   * @returns True if no changes are detected
   */
  hasNoChanges(): boolean {
    if (!this.currentProjectModel) {
      return false;
    }

    // If no action response data, check if we have actions that indicate no changes
    if (!this.actionResponseData) {
      if (this.hasActions()) {
        const actions = this.getActions();
        if (actions.length > 0) {
          const firstAction = actions[0] as Record<string, unknown>;
          const actionBody = firstAction.body as Record<string, unknown>;
          const message = actionBody?.message as string;
          // Check if the message indicates no changes
          return !!(message && (
            message.toLowerCase().includes('no changes') ||
            message.toLowerCase().includes('already exists') ||
            message.toLowerCase().includes('no updates needed')
          ));
        }
      }
      return false;
    }

    const currentFields = this.getCurrentModelFields();
    const hasNewFields = currentFields.some(field => {
      const fieldObj = field as Record<string, unknown>;
      return fieldObj.status === 'new';
    });
    const hasDeletedFields = currentFields.some(field => {
      const fieldObj = field as Record<string, unknown>;
      return fieldObj.status === 'deleted';
    });

    return !hasNewFields && !hasDeletedFields;
  }

  /**
   * Gets the no-changes message from the action response
   * @returns Message indicating no changes were made
   */
  getNoChangesMessage(): string {
    if (!this.hasActions()) {
      return 'No changes were detected in the data model.';
    }

    const actions = this.getActions();
    if (actions.length === 0) {
      return 'No changes were detected in the data model.';
    }

    const firstAction = actions[0] as Record<string, unknown>;
    const actionBody = firstAction.body as Record<string, unknown>;
    const message = actionBody?.message as string;

    return message || 'No changes were detected in the data model.';
  }

  /**
   * Checks if a specific model has been created successfully
   * @param model - The model object to check
   * @returns True if the model has been created successfully
   */
  isModelCreated(model: unknown | null): boolean {
    const modelName = this.extractModelName(model);
    return modelName ? this.createdModelNames.has(modelName) : false;
  }

  /**
   * Extracts model name from a model object
   * @param model - Model object or null
   * @returns Model name or null
   */
  private extractModelName(model: unknown | null): string | null {
    if (!model || typeof model !== 'object') {
      return null;
    }
    const modelObj = model as Record<string, unknown>;
    return (modelObj.modelName || modelObj.name || null) as string | null;
  }

  /**
   * Filters response data to include only the selected model and its related relations
   * @param responseItem - The selected model item (null for relations-only case)
   * @returns Filtered response data containing only the selected model and its relations
   */
  private filterResponseDataForModel(responseItem: AIResponseStructure | null | unknown): unknown {
    if (!this.responseData || typeof this.responseData !== 'object') {
      return this.responseData;
    }

    const dataObj = this.responseData as Record<string, unknown>;
    const generated = dataObj.generated as Record<string, unknown>;
    if (!generated) {
      return this.responseData;
    }

    const selectedModelName = this.extractModelName(responseItem);
    const isRelationsOnly = !selectedModelName;
    const allRelations = (generated.relations || []) as unknown[];

    // Relations-only case: return only relations, no models
    if (isRelationsOnly) {
      return {
        ...dataObj,
        generated: {
          ...generated,
          models: [],
          relations: allRelations
        }
      };
    }

    // Model case: return only selected model and its relations
    const allModels = (generated.models || []) as unknown[];

    const filteredModels = allModels.filter(model =>
      this.extractModelName(model) === selectedModelName
    );

    const filteredRelations = allRelations.filter((relation: unknown) => {
      const rel = relation as Record<string, unknown>;
      return rel.sourceModel === selectedModelName || rel.targetModel === selectedModelName;
    });

    return {
      ...dataObj,
      generated: {
        ...generated,
        models: filteredModels,
        relations: filteredRelations
      }
    };
  }

}
